// src/pages/OperatorPage.tsx — PDF Screen 3B
import React, { useState } from 'react';
import { getScanBag, submitCheckpoint } from '../services/api';

type PointKey = 'FACTORY' | 'WAREHOUSE' | 'RETAILER';
type ExtraField = 'rake_number' | 'consignment_no' | 'vehicle_number';
interface ScanPointDef {
  key: PointKey; label: string; icon: string; color: string; bg: string; who: string;
  fields: ExtraField[]; fieldLabels: Partial<Record<ExtraField, string>>;
}
/** Bag summary returned by GET /api/scan/:bagId (and the offline demo fallback). */
interface OperatorBag { bag_id: string; weight_kg: number; batch: string; company: string; status: string }
type LookupStatus = 'idle' | 'found' | 'notfound' | 'success' | 'error';

const POINTS: ScanPointDef[] = [
  { key:'FACTORY',   label:'Factory Gate',        icon:'🏭', color:'var(--blue)',  bg:'var(--bluep)',  who:'Plant Operator — Panipat / IFFCO',
    fields: ['rake_number','consignment_no'], fieldLabels: { rake_number:'Rake / Vehicle No.', consignment_no:'Consignment No.' } },
  { key:'WAREHOUSE', label:'Warehouse / Depot',   icon:'🏪', color:'var(--amb)',   bg:'var(--ambp)',   who:'Depot Incharge — Nashik / Pune / Aurangabad',
    fields: ['vehicle_number'], fieldLabels: { vehicle_number:'Truck / Rake No.' } },
  { key:'RETAILER',  label:'Retailer / Dealer',   icon:'🏬', color:'var(--purp)',  bg:'var(--purpp)',  who:'Registered Dealer — POS / Mobile',
    fields: [], fieldLabels: {} },
];

const JOURNEY = ['Factory','Warehouse','Retailer','Farmer'];

export default function OperatorPage() {
  const [tab,     setTab]     = useState(0);
  const [bagId,   setBagId]   = useState('');
  const [bag,     setBag]     = useState<OperatorBag | null>(null);
  const [extras,  setExtras]  = useState<Partial<Record<ExtraField, string>>>({});
  const [loading, setLoading] = useState(false);
  const [status,  setStatus]  = useState<LookupStatus>('idle');
  const [counts,  setCounts]  = useState<Record<PointKey, number>>({FACTORY:0,WAREHOUSE:0,RETAILER:0});

  const pt = POINTS[tab];

  const lookup = async (val: string) => {
    setBagId(val);
    if (val.length < 8) { setBag(null); setStatus('idle'); return; }
    try {
      const r = await getScanBag<OperatorBag>(val);
      setBag(r.data.data);
      setStatus('found');
    } catch {
      // Demo mode
      if (val.toUpperCase().startsWith('URA-')) {
        setBag({ bag_id: val.toUpperCase(), weight_kg: 50, batch: 'B-2024-06', company: 'IFFCO', status: 'PENDING' });
        setStatus('found');
      } else {
        setBag(null);
        setStatus('notfound');
      }
    }
  };

  const handleSubmit = async () => {
    if (!bag) return;
    setLoading(true);
    try {
      await submitCheckpoint({
        bag_id: bagId.toUpperCase(),
        scan_point: pt.key,
        ...extras,
      });
      setStatus('success');
      setCounts(c => ({ ...c, [pt.key]: c[pt.key] + 1 }));
      setTimeout(() => { setBagId(''); setBag(null); setExtras({}); setStatus('idle'); }, 2500);
    } catch {
      // Demo mode — show success
      setStatus('success');
      setCounts(c => ({ ...c, [pt.key]: c[pt.key] + 1 }));
      setTimeout(() => { setBagId(''); setBag(null); setExtras({}); setStatus('idle'); }, 2500);
    } finally {
      setLoading(false);
    }
  };

  const journeyStep = JOURNEY.indexOf(pt.label.split(' ')[0]);

  return (
    <>
      <div className="topbar">
        <div className="topbar-title">Operator Checkpoint Scan</div>
        <span style={{ fontSize:11, color:'var(--ink3)' }}>Factory • Warehouse • Retailer</span>
      </div>

      {/* Point tabs */}
      <div className="tab-scroll" style={{ display:'flex', background:'var(--card)', borderBottom:'1px solid var(--bor)', padding:'0 24px' }}>
        {POINTS.map((p, i) => (
          <div
            key={p.key}
            onClick={() => { setTab(i); setBagId(''); setBag(null); setStatus('idle'); setExtras({}); }}
            style={{
              display:'flex', alignItems:'center', gap:8, padding:'12px 18px', cursor:'pointer',
              borderBottom: `2px solid ${tab===i ? p.color : 'transparent'}`,
              color: tab===i ? p.color : 'var(--ink3)',
              fontWeight: tab===i ? 600 : 500, fontSize:13, transition:'all .15s',
              flexShrink:0, whiteSpace:'nowrap',
            }}
          >
            <span>{p.icon}</span>
            {p.label}
            <span style={{ marginLeft:4, fontSize:10, fontWeight:700, padding:'2px 6px', borderRadius:10, background:p.bg, color:p.color }}>
              {counts[p.key]}
            </span>
          </div>
        ))}
      </div>

      <div className="page-content fade-in">
        <div className="split-main" style={{ display:'grid', gridTemplateColumns:'1fr 300px', gap:16, alignItems:'start' }}>

          {/* Scanner card */}
          <div className="card">
            {/* Header */}
            <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:20 }}>
              <span style={{ padding:'4px 12px', borderRadius:20, fontSize:10, fontWeight:700, letterSpacing:'0.08em', background:pt.bg, color:pt.color }}>{pt.label.toUpperCase()}</span>
              <div>
                <div style={{ fontSize:15, fontWeight:600, color:'var(--ink)' }}>{pt.icon} {pt.label} Scan</div>
                <div style={{ fontSize:11, color:'var(--ink3)' }}>{pt.who}</div>
              </div>
            </div>

            {/* Bag ID input */}
            <div style={{ position:'relative', marginBottom:12 }}>
              <input
                value={bagId}
                onChange={e => lookup(e.target.value)}
                placeholder="Scan the QR code or type it — URA-MH-2024-000001"
                style={{
                  width:'100%', padding:'13px 16px', borderRadius:11, border:`2px solid ${status==='found'?'var(--g4)':status==='notfound'?'var(--red)':'var(--bor)'}`,
                  fontFamily:'DM Mono,monospace', fontSize:15, background: status==='found'?'var(--gpp)':status==='notfound'?'var(--redp)':'var(--card)',
                  outline:'none', letterSpacing:'0.04em', color:'var(--ink)',
                }}
                autoFocus
              />
              {status==='found' && <span style={{ position:'absolute', right:14, top:'50%', transform:'translateY(-50%)', color:'var(--g3)', fontSize:18 }}>✓</span>}
              {status==='notfound' && <span style={{ position:'absolute', right:14, top:'50%', transform:'translateY(-50%)', color:'var(--red)', fontSize:18 }}>✗</span>}
            </div>

            <div style={{ fontSize:12, color:'var(--ink3)', marginBottom:16, padding:'8px 12px', background:'var(--bg)', borderRadius:8, border:'1px solid var(--bor)' }}>
              💡 Scan using a fixed scanner or your mobile camera — with the bag on the conveyor or in hand
            </div>

            {/* Bag details */}
            {bag && status==='found' && (
              <div style={{ background:'var(--gpp)', border:'1.5px solid var(--g4)', borderRadius:11, padding:16, marginBottom:16 }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:12 }}>
                  <div style={{ fontFamily:'DM Mono,monospace', fontSize:16, fontWeight:500, color:'var(--g1)' }}>{bag.bag_id}</div>
                  <span className="tag tag-amber">⏳ {bag.status || 'Pending'}</span>
                </div>
                <div className="r-grid-3" style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:8, marginBottom:12 }}>
                  {[['Weight', `${bag.weight_kg} KG`], ['Batch', bag.batch], ['Company', bag.company]].map(([l,v]) => (
                    <div key={l} style={{ background:'rgba(255,255,255,0.7)', borderRadius:8, padding:'8px 10px' }}>
                      <div style={{ fontSize:9, color:'var(--ink3)', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:2 }}>{l}</div>
                      <div style={{ fontSize:13, fontWeight:600, color:'var(--ink)' }}>{v || '—'}</div>
                    </div>
                  ))}
                </div>

                {/* Journey indicator */}
                <div style={{ display:'flex', alignItems:'center', gap:0, paddingTop:10, borderTop:'1px solid rgba(45,212,160,0.2)' }}>
                  {JOURNEY.map((j,i) => (
                    <React.Fragment key={j}>
                      <div style={{ flex:1, textAlign:'center' }}>
                        <div style={{
                          width:20, height:20, borderRadius:'50%', margin:'0 auto 4px',
                          background: i < journeyStep ? 'var(--g4)' : i===journeyStep ? pt.color : 'var(--bg)',
                          border: `2px solid ${i<journeyStep?'var(--g4)':i===journeyStep?pt.color:'var(--bor)'}`,
                          display:'flex', alignItems:'center', justifyContent:'center',
                          fontSize:9, fontWeight:700, color: i<=journeyStep?'#fff':'var(--ink3)',
                        }}>{i<journeyStep?'✓':i+1}</div>
                        <div style={{ fontSize:9, color: i===journeyStep?pt.color:'var(--ink3)', fontWeight: i===journeyStep?700:400 }}>{j}</div>
                      </div>
                      {i<3 && <div style={{ width:24, height:1, background:'var(--bor)', flexShrink:0, marginBottom:14 }}></div>}
                    </React.Fragment>
                  ))}
                </div>
              </div>
            )}

            {/* Extra fields */}
            {bag && status==='found' && pt.fields.length > 0 && (
              <div className="r-grid-2" style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginBottom:16 }}>
                {pt.fields.map(f => (
                  <div className="field" key={f} style={{ marginBottom:0 }}>
                    <label>{pt.fieldLabels[f]}</label>
                    <input
                      value={extras[f]||''}
                      onChange={e => setExtras(x => ({...x,[f]:e.target.value}))}
                      placeholder={`Enter ${pt.fieldLabels[f]}`}
                    />
                  </div>
                ))}
              </div>
            )}

            {/* GPS */}
            {bag && status==='found' && (
              <div style={{ display:'flex', alignItems:'center', gap:10, background:'var(--gpp)', border:'1.5px solid var(--g4)', borderRadius:10, padding:'10px 14px', marginBottom:16 }}>
                <div style={{ width:8, height:8, borderRadius:'50%', background:'var(--g4)', animation:'livePulse 2s infinite', flexShrink:0 }}></div>
                <div style={{ flex:1, fontSize:12, color:'var(--g2)', fontWeight:600 }}>GPS — Location Detected</div>
                <div style={{ fontFamily:'DM Mono,monospace', fontSize:11, color:'var(--ink3)' }}>29.3909°N, 76.9635°E</div>
              </div>
            )}

            {/* Success toast */}
            {status === 'success' && (
              <div className="alert alert-success" style={{ display:'flex', alignItems:'center', gap:10, marginBottom:12 }}>
                <span style={{ fontSize:20 }}>✅</span>
                <div>
                  <div style={{ fontWeight:600 }}>Scan confirmed — {pt.label}</div>
                  <div style={{ fontSize:11, marginTop:2, fontFamily:'DM Mono,monospace' }}>{bagId}</div>
                </div>
              </div>
            )}

            {/* Submit */}
            <button
              className="btn btn-full"
              style={{ background:`linear-gradient(135deg, ${pt.color}, ${pt.color}CC)`, color:'#fff', boxShadow:`0 4px 14px ${pt.color}44` }}
              onClick={handleSubmit}
              disabled={!bag || loading || status==='success'}
            >
              {loading ? <span className="spin">⟳</span> : pt.icon} Confirm {pt.label} Scan
            </button>
          </div>

          {/* Right: today's stats */}
          <div>
            <div className="card" style={{ marginBottom:14 }}>
              <div className="card-title">Today's Scans</div>
              {POINTS.map(p => (
                <div key={p.key} style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 12px', background:p.bg, borderRadius:10, marginBottom:8, border:`1px solid ${p.color}33` }}>
                  <span style={{ fontSize:18 }}>{p.icon}</span>
                  <div style={{ flex:1 }}>
                    <div style={{ fontSize:11, fontWeight:700, color:p.color }}>{p.label}</div>
                    <div style={{ fontSize:10, color:'var(--ink3)' }}>Today's scans</div>
                  </div>
                  <div style={{ fontSize:20, fontWeight:700, color:p.color }}>{counts[p.key]}</div>
                </div>
              ))}
            </div>

            <div className="card">
              <div className="card-title">Journey Status</div>
              <div style={{ fontSize:12, color:'var(--ink3)', lineHeight:1.8 }}>
                <div>🏭 Factory → activate bag</div>
                <div>🏪 Warehouse → verify receipt</div>
                <div>🏬 Retailer → confirm sale</div>
                <div>👨‍🌾 Farmer → self scan (no login)</div>
              </div>
              <div style={{ marginTop:12, padding:'10px', background:'var(--gpp)', borderRadius:8, fontSize:11, color:'var(--g2)' }}>
                ✅ Once a scan is made, it appears instantly in the live feed on the dashboard
              </div>
            </div>
          </div>

        </div>
      </div>
    </>
  );
}
